import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Check, CloudOff, Dot } from "lucide-react";

const RELATIVE_REFRESH_MS = 30000;

/** "hace un momento" / "hace 3 min" / "hace 2 h" a partir de un timestamp. */
function formatRelative(timestamp) {
    if (!timestamp) return null;

    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 60) return "hace un momento";

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `hace ${minutes} min`;

    const hours = Math.floor(minutes / 60);
    return `hace ${hours} h`;
}

/**
 * SaveStatus — indicador pasivo del estado de guardado.
 *
 * Comunica sin interrumpir: nunca abre diálogos. En estado de error ofrece
 * reintentar, que es la única acción que el usuario puede tomar desde aquí.
 */
function SaveStatus({ status = "saved", lastSavedAt = null, onRetry = null, className = "" }) {
    // El texto relativo caduca solo: se refresca aunque no cambie ninguna prop.
    const [, setTick] = useState(0);

    useEffect(() => {
        if (status !== "saved" || !lastSavedAt) return undefined;
        const interval = setInterval(() => setTick((value) => value + 1), RELATIVE_REFRESH_MS);
        return () => clearInterval(interval);
    }, [status, lastSavedAt]);

    if (status === "saving") {
        return (
            <span className={`flex items-center gap-1.5 text-xs text-base-content/60 ${className}`}>
                <span className="loading loading-spinner loading-xs" />
                Guardando…
            </span>
        );
    }

    if (status === "dirty") {
        return (
            <span className={`flex items-center gap-0.5 text-xs text-warning ${className}`}>
                <Dot className="h-4 w-4" />
                Sin guardar
            </span>
        );
    }

    if (status === "error") {
        return (
            <span className={`flex items-center gap-1.5 text-xs text-error ${className}`}>
                <CloudOff className="h-3.5 w-3.5" />
                No se pudo guardar
                {onRetry && (
                    <button type="button" onClick={onRetry} className="link link-error font-medium">
                        Reintentar
                    </button>
                )}
            </span>
        );
    }

    const relative = formatRelative(lastSavedAt);

    return (
        <span className={`flex items-center gap-1.5 text-xs text-success ${className}`}>
            <Check className="h-3.5 w-3.5" />
            {relative ? `Guardado ${relative}` : "Guardado"}
        </span>
    );
}

SaveStatus.propTypes = {
    status: PropTypes.oneOf(["saved", "dirty", "saving", "error"]),
    lastSavedAt: PropTypes.number,
    onRetry: PropTypes.func,
    className: PropTypes.string,
};

export default SaveStatus;
